import React, { useState, useRef, useMemo, useEffect } from 'react';

export default function FuzzyProjectSelect({ projects, value, onChange, placeholder = '选择项目' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(0);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // 过滤项目列表（支持模糊搜索：项目名称/ID）
  const filteredProjects = useMemo(() => {
    if (!searchText) return projects;
    const lower = searchText.toLowerCase();
    return projects.filter(p => 
      (p.name && p.name.toLowerCase().includes(lower)) ||
      (p.id && p.id.toLowerCase().includes(lower))
    );
  }, [projects, searchText]);

  // 获取显示文本
  const displayText = value ? (projects.find(p => p.id === value)?.name || value) : placeholder;

  // 点击外部关闭
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 打开时聚焦输入框
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      setHighlightIndex(0);
    }
  }, [isOpen]);

  const handleSelect = (projectId) => {
    onChange(projectId);
    setIsOpen(false);
    setSearchText('');
  };

  const handleKeyDown = (e) => {
    if (!isOpen) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightIndex(prev => Math.min(prev + 1, filteredProjects.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredProjects[highlightIndex]) {
          handleSelect(filteredProjects[highlightIndex].id);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
    }
  };

  return (
    <div className="project-select-wrapper" style={{ display: "inline-flex", verticalAlign: "middle", minWidth: "260px" }} ref={wrapperRef}>
      <div
        className="project-select-trigger"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="project-select-text">{displayText}</span>
        <span className="project-select-arrow">{isOpen ? '▲' : '▼'}</span>
      </div>
      {isOpen && (
        <div className="project-select-dropdown">
          <div className="project-select-search">
            <input
              ref={inputRef}
              type="text"
              placeholder="搜索项目..."
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setHighlightIndex(0);
              }}
              onKeyDown={handleKeyDown}
            />
          </div>
          <div className="project-select-list">
            {filteredProjects.map((project, index) => (
              <div
                key={project.id}
                className={`project-select-option ${project.id === value ? 'selected' : ''} ${index === highlightIndex ? 'highlight' : ''}`}
                onClick={() => handleSelect(project.id)}
                onMouseEnter={() => setHighlightIndex(index)}
              >
                {project.name}
              </div>
            ))}
            {filteredProjects.length === 0 && (
              <div className="project-select-empty">无匹配项目</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
